"use client"

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react"
import { DexieAdapter } from "../../../lib/offline/adapters/dexie-adapter"
import { DexieReactiveAdapter } from "../../../lib/offline/adapters/dexie-reactive"
import { FetchHttpClient } from "../../../lib/offline/adapters/fetch-http-client"
import { SSETransport } from "../../../lib/offline/adapters/sse-transport"
import { ChangeQueue } from "../../../lib/offline/change-queue"
import { offlineDb, openOfflineDb } from "../../../lib/offline/db"
import { setActiveSyncEngine } from "../../../lib/offline/reset"
import { SyncEngine } from "../../../lib/offline/sync-engine"
import type {
  HttpClientAdapter,
  ReactiveHookAdapter,
  RealtimeTransportAdapter,
  StorageAdapter,
  TelemetrySink,
} from "../../../lib/offline/types"
import { workflowConfig } from "../../../lib/offline/resources/ai/workflow/config"
import { executionConfig } from "../../../lib/offline/resources/ai/execution/config"
import { chatSessionConfig } from "../../../lib/offline/resources/ai/chat-session/config"
import { chatMessageConfig } from "../../../lib/offline/resources/ai/chat-message/config"
import { chatExecutionConfig } from "../../../lib/offline/resources/ai/chat-execution/config"
import { chatMediaConfig } from "../../../lib/offline/resources/ai/chat-media/config"
import { proposalBatchConfig } from "../../../lib/offline/resources/ai/proposal-batch/config"
import { productConfig } from "../../../lib/offline/resources/inventory/product/config"
import { unitConfig } from "../../../lib/offline/resources/inventory/unit/config"
import { productUnitConfig } from "../../../lib/offline/resources/inventory/product-unit/config"
import { locationConfig } from "../../../lib/offline/resources/inventory/location/config"
import { lotConfig } from "../../../lib/offline/resources/inventory/lot/config"
import { stockConfig } from "../../../lib/offline/resources/inventory/stock/config"
import { priceConfig } from "../../../lib/offline/resources/pricing/price/config"
import { partnerConfig } from "../../../lib/offline/resources/crm/partner/config"
import { partnerAddressConfig } from "../../../lib/offline/resources/crm/partner-address/config"
import { emailContactConfig } from "../../../lib/offline/resources/communications/email-contact/config"
import { partnerContactConfig } from "../../../lib/offline/resources/crm/partner-contact/config"
import { clientGroupConfig } from "../../../lib/offline/resources/crm/client-group/config"
import { partnerAccountConfig } from "../../../lib/offline/resources/crm/partner-account/config"
import { emailConfig } from "../../../lib/offline/resources/communications/email/config"
import { smsChatConfig } from "../../../lib/offline/resources/communications/sms-chat/config"
import { smsMessageConfig } from "../../../lib/offline/resources/communications/sms-message/config"
import { whatsappChatConfig } from "../../../lib/offline/resources/communications/whatsapp-chat/config"
import { whatsappMessageConfig } from "../../../lib/offline/resources/communications/whatsapp-message/config"
import { preOrderConfig } from "../../../lib/offline/resources/orders/pre-order/config"
import { preOrderFlowConfig } from "../../../lib/offline/resources/orders/pre-order-flow/config"
import { preOrderMessageConfig } from "../../../lib/offline/resources/orders/pre-order-message/config"
import { preOrderSmsMessageConfig } from "../../../lib/offline/resources/orders/pre-order-sms-message/config"
import { preOrderEmailConfig } from "../../../lib/offline/resources/orders/pre-order-email/config"
import { feedbackConfig } from "../../../lib/offline/resources/core/feedback/config"
import { feedbackMessageConfig } from "../../../lib/offline/resources/core/feedback-message/config"
import { useClearUnreadOnFocus } from "../hooks/useClearUnreadOnFocus"
import { preOrderSuggestionConfig } from "../../../lib/offline/resources/ai/pre-order-suggestion/config"
import { preOrderFlowSuggestionConfig } from "../../../lib/offline/resources/ai/pre-order-flow-suggestion/config"
import { productPriceSuggestionConfig } from "../../../lib/offline/resources/ai/product-price-suggestion/config"
import { partnerProductPriceSuggestionConfig } from "../../../lib/offline/resources/ai/partner-product-price-suggestion/config"
import { clientGroupProductPriceSuggestionConfig } from "../../../lib/offline/resources/ai/client-group-product-price-suggestion/config"
import { flowSuggestionConfig } from "../../../lib/offline/resources/ai/flow-suggestion/config"

/**
 * Resource-name manifest grouped by cold-start wave. Single source of truth
 * for UIs that need to display per-wave progress (e.g. the global boot
 * screen). The strings here MUST match each `*Config.name` field; if they
 * drift, the boot screen will show stale resource counts during the cold
 * start.
 *
 * Order is informational only — it matches the `engine.registerResource`
 * calls below for readability.
 */
export const SYNC_WAVES = {
  critical: [
    productConfig.name,
    unitConfig.name,
    stockConfig.name,
    priceConfig.name,
    partnerConfig.name,
    partnerAddressConfig.name,
    emailContactConfig.name,
    partnerContactConfig.name,
    clientGroupConfig.name,
    partnerAccountConfig.name,
    preOrderConfig.name,
    preOrderFlowConfig.name,
    preOrderMessageConfig.name,
    preOrderSmsMessageConfig.name,
    preOrderEmailConfig.name,
  ],
  warm: [
    workflowConfig.name,
    executionConfig.name,
    smsChatConfig.name,
    whatsappChatConfig.name,
    locationConfig.name,
    lotConfig.name,
    productUnitConfig.name,
    proposalBatchConfig.name,
    preOrderSuggestionConfig.name,
    preOrderFlowSuggestionConfig.name,
    productPriceSuggestionConfig.name,
    partnerProductPriceSuggestionConfig.name,
    clientGroupProductPriceSuggestionConfig.name,
    flowSuggestionConfig.name,
  ],
  background: [
    chatSessionConfig.name,
    chatMessageConfig.name,
    chatExecutionConfig.name,
    chatMediaConfig.name,
    emailConfig.name,
    smsMessageConfig.name,
    whatsappMessageConfig.name,
    feedbackConfig.name,
    feedbackMessageConfig.name,
  ],
} as const

/**
 * Resource-name → Dexie table-name map. Resource names are kebab-case
 * plural (used as SSE event keys), but many tables are camelCase
 * (`preOrderFlows`, `partnerAccounts`, `chatSessions`, …). Hooks that
 * need to read a resource's row count (`useResourceRowCount`) MUST
 * route through this map; calling `offlineDb.table(resourceName)`
 * directly returns null for every config whose name differs from its
 * tableName.
 */
export const RESOURCE_TABLES: Readonly<Record<string, string>> = {
  [productConfig.name]: productConfig.tableName,
  [unitConfig.name]: unitConfig.tableName,
  [stockConfig.name]: stockConfig.tableName,
  [priceConfig.name]: priceConfig.tableName,
  [partnerConfig.name]: partnerConfig.tableName,
  [partnerAddressConfig.name]: partnerAddressConfig.tableName,
  [emailContactConfig.name]: emailContactConfig.tableName,
  [partnerContactConfig.name]: partnerContactConfig.tableName,
  [clientGroupConfig.name]: clientGroupConfig.tableName,
  [partnerAccountConfig.name]: partnerAccountConfig.tableName,
  [preOrderConfig.name]: preOrderConfig.tableName,
  [preOrderFlowConfig.name]: preOrderFlowConfig.tableName,
  [preOrderMessageConfig.name]: preOrderMessageConfig.tableName,
  [preOrderSmsMessageConfig.name]: preOrderSmsMessageConfig.tableName,
  [preOrderEmailConfig.name]: preOrderEmailConfig.tableName,
  [workflowConfig.name]: workflowConfig.tableName,
  [executionConfig.name]: executionConfig.tableName,
  [smsChatConfig.name]: smsChatConfig.tableName,
  [whatsappChatConfig.name]: whatsappChatConfig.tableName,
  [locationConfig.name]: locationConfig.tableName,
  [lotConfig.name]: lotConfig.tableName,
  [productUnitConfig.name]: productUnitConfig.tableName,
  [proposalBatchConfig.name]: proposalBatchConfig.tableName,
  [chatSessionConfig.name]: chatSessionConfig.tableName,
  [chatMessageConfig.name]: chatMessageConfig.tableName,
  [chatExecutionConfig.name]: chatExecutionConfig.tableName,
  [chatMediaConfig.name]: chatMediaConfig.tableName,
  [emailConfig.name]: emailConfig.tableName,
  [smsMessageConfig.name]: smsMessageConfig.tableName,
  [whatsappMessageConfig.name]: whatsappMessageConfig.tableName,
  [feedbackConfig.name]: feedbackConfig.tableName,
  [feedbackMessageConfig.name]: feedbackMessageConfig.tableName,
  [preOrderSuggestionConfig.name]: preOrderSuggestionConfig.tableName,
  [preOrderFlowSuggestionConfig.name]: preOrderFlowSuggestionConfig.tableName,
  [productPriceSuggestionConfig.name]: productPriceSuggestionConfig.tableName,
  [partnerProductPriceSuggestionConfig.name]:
    partnerProductPriceSuggestionConfig.tableName,
  [clientGroupProductPriceSuggestionConfig.name]:
    clientGroupProductPriceSuggestionConfig.tableName,
  [flowSuggestionConfig.name]: flowSuggestionConfig.tableName,
} as const

export interface AISyncContextValue {
  engine: SyncEngine
  storage: StorageAdapter
  changeQueue: ChangeQueue
  reactive: ReactiveHookAdapter
}

const AISyncContext = createContext<AISyncContextValue | null>(null)

export function useAISync(): AISyncContextValue {
  const ctx = useContext(AISyncContext)
  if (!ctx) {
    throw new Error("useAISync must be used within AISyncProvider")
  }
  return ctx
}

export interface AISyncProviderProps {
  children: ReactNode
  overrides?: {
    storage?: StorageAdapter
    httpClient?: HttpClientAdapter
    transport?: RealtimeTransportAdapter
    reactive?: ReactiveHookAdapter
    /**
     * Operational-telemetry sink for the SyncEngine (db-closed, resync,
     * reconnect). The consumer (`frontend/`) injects a PostHog-backed
     * implementation; omitted in tests/SSR, where the engine no-ops.
     */
    telemetry?: TelemetrySink
  }
}

export function AISyncProvider({ children, overrides }: AISyncProviderProps) {
  const ctxRef = useRef<AISyncContextValue | null>(null)

  if (!ctxRef.current) {
    const getAuthToken = () => {
      if (typeof window === "undefined") return null
      return localStorage.getItem("bearer")
    }

    // V2 API URL: Vite env (build-time) → Next.js env → port-based fallback
    const v2BaseUrl =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (import.meta as any).env?.VITE_V2_API_URL ??
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((globalThis as any).process?.env?.NEXT_PUBLIC_V2_API_URL as
        | string
        | undefined) ??
      (typeof window !== "undefined" && window.location.port === "3000"
        ? "http://localhost:3001"
        : "")

    const storage = overrides?.storage ?? new DexieAdapter(offlineDb)
    const httpClient =
      overrides?.httpClient ?? new FetchHttpClient(v2BaseUrl, getAuthToken)
    const transport = overrides?.transport ?? new SSETransport()
    const reactive = overrides?.reactive ?? new DexieReactiveAdapter(offlineDb)
    const changeQueue = new ChangeQueue(storage)

    const sseUrl = `${v2BaseUrl}/api/v2/sync/stream`

    const engine = new SyncEngine({
      storage,
      httpClient,
      transport,
      changeQueue,
      sseUrl,
      getAuthToken,
      telemetry: overrides?.telemetry,
    })

    // Critical: recap-essential data — pulled first so the user-visible page
    // can hydrate without competing with heavy chat/AI history.
    engine.registerResource({ ...productConfig, priority: "critical" })
    engine.registerResource({ ...unitConfig, priority: "critical" })
    // Stocks is user-visible on the recap/stock surfaces — keep critical so
    // the page hydrates without a follow-up fetch.
    engine.registerResource({ ...stockConfig, priority: "critical" })
    // Prices are recap-essential — the per-row effective-price resolver
    // joins flows against `prices` on every recap render. Pulling in the
    // critical wave keeps the recap shell from flashing default prices
    // before the override hydrates.
    engine.registerResource({ ...priceConfig, priority: "critical" })
    engine.registerResource({ ...partnerConfig, priority: "critical" })
    engine.registerResource({ ...partnerAddressConfig, priority: "critical" })
    engine.registerResource({ ...emailContactConfig, priority: "critical" })
    engine.registerResource({ ...partnerContactConfig, priority: "critical" })
    // Client groups join with `partner.clientGroupIds` at render time on
    // every partner-list / detail / recap surface — pull in the critical
    // wave so badges hydrate alongside the partner shell.
    engine.registerResource({ ...clientGroupConfig, priority: "critical" })
    // Partner accounts (encours) drive the recap PartnerAccountBadge. Small
    // payload (~1 row per partner), recap-essential — keep critical so the
    // badges hydrate alongside the partner shell.
    engine.registerResource({ ...partnerAccountConfig, priority: "critical" })
    // Pre-orders + flows are recap-essential — pulled in the critical wave
    // so the recap shell hydrates without waiting on chat/AI history.
    engine.registerResource({ ...preOrderConfig, priority: "critical" })
    engine.registerResource({ ...preOrderFlowConfig, priority: "critical" })
    // Pre-order ↔ message junctions — small payload, drives recap "linked
    // messages" and chat-page "N pre-orders linked" badges. Critical so the
    // badges hydrate alongside the pre-order shell, not after.
    engine.registerResource({ ...preOrderMessageConfig, priority: "critical" })
    engine.registerResource({
      ...preOrderSmsMessageConfig,
      priority: "critical",
    })
    engine.registerResource({ ...preOrderEmailConfig, priority: "critical" })

    // Warm: AI structure + chat-list previews — small payloads, useful for
    // the AI/chat/recap shells without blocking the critical wave.
    engine.registerResource({ ...workflowConfig, priority: "warm" })
    engine.registerResource({ ...executionConfig, priority: "warm" })
    engine.registerResource({ ...smsChatConfig, priority: "warm" })
    engine.registerResource({ ...whatsappChatConfig, priority: "warm" })
    // Locations + lots are FK targets for stocks but rarely change and are
    // only displayed once a stock row is opened — warm avoids piling them
    // onto the cold-start critical wave (caused docker OOM, see PR history).
    engine.registerResource({ ...locationConfig, priority: "warm" })
    engine.registerResource({ ...lotConfig, priority: "warm" })
    // product_units carries the per-row weight the ERP conversion sync
    // writes. Recap currently reads the embedded `Product.productUnits[]`
    // ref shape via the product feed, but optimistic writes + SSE deltas
    // need a first-class store. Warm wave so updates land alongside the
    // parent product re-emit, not after.
    engine.registerResource({ ...productUnitConfig, priority: "warm" })
    // Pending AI proposals drive the linked-bar badge in conversation views
    // — pull them in the warm wave so the badge surfaces as soon as the
    // chat shell is interactive, not at the tail end of the cold-start.
    engine.registerResource({ ...proposalBatchConfig, priority: "warm" })
    // Typed AI suggestion sidecar rows — the recap reads pending ones per
    // pre-order to render the AI-proposed modify/delete diffs. Warm so they
    // hydrate alongside the recap shell (pre-orders are critical), not at the
    // tail of the cold start.
    engine.registerResource({ ...preOrderSuggestionConfig, priority: "warm" })
    engine.registerResource({
      ...preOrderFlowSuggestionConfig,
      priority: "warm",
    })
    engine.registerResource({
      ...productPriceSuggestionConfig,
      priority: "warm",
    })
    engine.registerResource({
      ...partnerProductPriceSuggestionConfig,
      priority: "warm",
    })
    engine.registerResource({
      ...clientGroupProductPriceSuggestionConfig,
      priority: "warm",
    })
    engine.registerResource({ ...flowSuggestionConfig, priority: "warm" })

    engine.registerResource({ ...chatSessionConfig, priority: "background" })
    engine.registerResource({ ...chatMessageConfig, priority: "background" })
    engine.registerResource({ ...chatExecutionConfig, priority: "background" })
    engine.registerResource({ ...chatMediaConfig, priority: "background" })
    engine.registerResource({ ...emailConfig, priority: "background" })
    engine.registerResource({ ...smsMessageConfig, priority: "background" })
    engine.registerResource({
      ...whatsappMessageConfig,
      priority: "background",
    })
    // In-app feedback is a low-traffic, cross-cutting resource — background
    // wave so it doesn't compete with chat/email history on cold start.
    engine.registerResource({ ...feedbackConfig, priority: "background" })
    engine.registerResource({
      ...feedbackMessageConfig,
      priority: "background",
    })

    ctxRef.current = { engine, storage, changeQueue, reactive }
  }

  useEffect(() => {
    const { engine } = ctxRef.current!
    setActiveSyncEngine(engine)
    // Guards the deferred `start()` against a provider that unmounts (auth /
    // user switch) while the DB open is still pending: without it, the `finally`
    // would fire AFTER cleanup ran `engine.stop()`, restarting a stopped engine
    // and letting the torn-down provider reopen / repopulate IndexedDB behind
    // the new one. Set before `engine.stop()` in cleanup so the check below sees
    // it.
    let disposed = false
    // Open the DB under an independent watchdog first: a hung/blocked open that
    // never fires Dexie's `blocked` event would otherwise leave `start()` (and
    // the boot splash) waiting silently forever. On a healthy open this resolves
    // in milliseconds and `start()` proceeds immediately.
    void openOfflineDb().finally(() => {
      if (disposed) return
      void engine.start()
    })
    return () => {
      disposed = true
      setActiveSyncEngine(null)
      engine.stop()
    }
  }, [])

  return (
    <AISyncContext.Provider value={ctxRef.current}>
      <UnreadReconciler />
      {children}
    </AISyncContext.Provider>
  )
}

/**
 * Reconciles the active+focused chat session's `unread` flag to the DB. Mounted
 * once here (inside the context, so it can use `useChatMutations`) rather than
 * per-surface, so the clear-on-read works for both the /ai page and the floating
 * side panel without each having to wire it. Renders nothing.
 */
function UnreadReconciler() {
  useClearUnreadOnFocus()
  return null
}
